import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Flag } from 'lucide-react';
import { useAuth, axiosInstance as axios } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

export const ServiceBadges = ({ professional }) => (
  <div className="flex flex-wrap gap-2 mb-4" data-testid="service-badges">
    {professional.offers_free_education && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800" data-testid="badge-free-education">Free Educational Presentations</span>}
    {professional.offers_free_consultation && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800" data-testid="badge-intro-consultation">Free Introductory Consultation</span>}
    {professional.offers_paid_services && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800" data-testid="badge-paid-services">Paid Services — contracted directly with the professional</span>}
  </div>
);

export const ProDisclosures = ({ professional }) => (
  (professional.fee_disclosure || professional.conflicts_of_interest || professional.jurisdiction) ? (
    <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm space-y-1" data-testid="pro-disclosures">
      {professional.jurisdiction && <p><span className="font-semibold">Jurisdiction:</span> {professional.jurisdiction}</p>}
      {professional.fee_disclosure && <p><span className="font-semibold">Fees:</span> {professional.fee_disclosure}</p>}
      {professional.conflicts_of_interest && <p><span className="font-semibold">Conflicts of interest:</span> {professional.conflicts_of_interest}</p>}
      <p className="text-xs text-gray-500 pt-1">Paid services are agreed directly with the professional. Legacy Forge Family takes no commission.</p>
    </div>
  ) : null
);

export const ReportProfessionalButton = ({ professional, onReported }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('inappropriate_conduct');
  const [details, setDetails] = useState('');
  const [sending, setSending] = useState(false);
  if (user?.user_id && user.user_id === professional.user_id) return null;
  const submit = async () => {
    setSending(true);
    try {
      const r = await axios.post(`/api/directory/professionals/${professional.profile_id}/report`, { category, details });
      toast.success(r.data.message);
      setOpen(false);
      onReported?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Please add a few more details (at least 10 characters).');
    } finally {
      setSending(false);
    }
  };
  return (
    <>
      <button type="button" onClick={() => (user ? setOpen(true) : navigate('/login'))}
        className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:underline" data-testid="report-professional-btn">
        <Flag size={14} /> Report a concern
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="report-professional-dialog">
          <DialogHeader>
            <DialogTitle>Report {professional.name}</DialogTitle>
            <DialogDescription>Your report goes only to Legacy Forge Family. The listing is hidden while we review it.</DialogDescription>
          </DialogHeader>
          <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="report-category-select">
            <option value="inappropriate_conduct">Inappropriate conduct</option>
            <option value="misleading_information">Misleading information</option>
            <option value="other">Something else</option>
          </select>
          <textarea value={details} onChange={e => setDetails(e.target.value)} rows={4} placeholder="What happened?"
            className="w-full px-3 py-2 border rounded-lg mt-3" data-testid="report-details-input" />
          <button onClick={submit} disabled={sending || details.trim().length < 10}
            className="w-full mt-3 px-4 py-2.5 bg-red-600 text-white rounded-lg font-semibold disabled:opacity-50" data-testid="report-submit-btn">
            {sending ? 'Sending…' : 'Send report'}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
